import { useMutation, useQuery } from '@tanstack/react-query';
import { sendRequest } from 'api/sendRequest';

import { endpoints } from 'constants/endpoints';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { TEMPLATES_TYPE } from '.';

const getActiveTemplates = async ({ offset, limit, includeCount }) => {
  try {
    const url = endpoints.templates.engagement.activeTemplates;
    const response = await sendRequest({
      url,
      method: 'GET',
      credentials: 'include',
      queryParams: {
        offset,
        limit,
        include_count: includeCount,
      },
    });

    return response.entity;
  } catch (err) {
    console.error('Error getting active templates', err);
    throw err;
  }
};

const getArchivedTemplates = async ({ offset, limit, includeCount }) => {
  try {
    const url = endpoints.templates.engagement.archivedTemplates;
    const response = await sendRequest({
      url,
      method: 'GET',
      credentials: 'include',
      queryParams: {
        offset,
        limit,
        include_count: includeCount,
      },
    });

    return response.entity;
  } catch (err) {
    console.error('Error getting archived templates', err);
    throw err;
  }
};

export const useGetEngagementTemplates = ({
  type = TEMPLATES_TYPE.ACTIVE,
  offset = 0,
  limit = 500,
  includeCount = false,
}: {
  type: keyof typeof TEMPLATES_TYPE;
  offset?: number;
  limit?: number;
  includeCount?: boolean;
}) =>
  useQuery({
    queryKey: [REACT_QUERY_KEYS.engagement.templates.getTemplates, offset, limit, includeCount, type],
    queryFn: () => {
      if (type === TEMPLATES_TYPE.ACTIVE) return getActiveTemplates({ offset, limit, includeCount });
      return getArchivedTemplates({ offset, limit, includeCount });
    },
  });

const archivedTemplate = async ({ templateId, shouldArchive }: { templateId: number; shouldArchive: boolean }) => {
  try {
    const url = endpoints.templates.engagement.archiveTemplate;
    const response = await sendRequest({
      url,
      credentials: 'include',
      method: 'POST',
      queryParams: {
        template_id: templateId,
        is_archive: shouldArchive,
      },
    });

    return response.entity;
  } catch (err) {
    console.error(`Error ${shouldArchive ? 'archiving' : 'unarchiving'} template`, err);
    throw err;
  }
};

export const useArchiveTemplate = () =>
  useMutation({
    mutationFn: ({ templateId, shouldArchive }: { templateId: number; shouldArchive: boolean }) =>
      archivedTemplate({ templateId, shouldArchive }),
  });

const duplicateTemplate = async ({ templateId }: { templateId: number }) => {
  try {
    const url = endpoints.templates.engagement.duplicateTemplate;
    const response = await sendRequest({
      url,
      credentials: 'include',
      method: 'POST',
      queryParams: {
        template_id: templateId,
      },
    });
    return response.entity;
  } catch (err) {
    console.error('Error duplicating template', err);
    throw err;
  }
};

export const useDuplicateTemplate = () =>
  useMutation({
    mutationFn: ({ templateId }: { templateId: number }) => duplicateTemplate({ templateId }),
  });
